import { Box, colors, FlexGrid } from '@alinta-digital/asg';
import {
  breakpoints,
  useBreakpoint,
} from '@alinta-digital/myaccount-frontend-shared';
import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';

interface SContainerProps {
  backgroundColor?: string;
  textColor?: string;
  boderWidth?: string;
}

const SContainer = styled.div<SContainerProps>`
  display: flex;
  gap: 8px;
  padding: 16px;
  background-color: ${(props) => props.backgroundColor || '#ffffff'};
  border: 1px solid;
  border-color: ${colors['grey-2']};
  border-left-width: ${(props) => props.boderWidth || '6px'};
  border-left-color: ${colors['colour-secondary']};
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;

  @media only screen and (min-width: ${breakpoints.md}) {
    gap: 16px;
    padding: 16px 24px;
  }

  svg {
    flex-shrink: 0;
  }

  .content {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: ${(props) => `${props.textColor}` || colors['colour-black']};
    margin-top: 4px;
    b {
      font-weight: 700;
      font-size: 14px;
      line-height: 20px;
    }
  }
`;

interface InfoMessageProps {
  style?: React.CSSProperties;
  backgroundColor?: string;
  textColor?: string;
  boderWidth?: string;
  'data-testid'?: string;
  icon?: React.ReactNode; // <-- Add icon prop
}

const InfoBanner = (
  props: PropsWithChildren<InfoMessageProps>
): JSX.Element => {
  const { icon, children, ...rest } = props;
  const responsiveSize = useBreakpoint();

  return (
    <Box
      flex="grow"
      margin={{
        horizontal: responsiveSize.isMobile
          ? 'large'
          : responsiveSize.isTablet
          ? 'large'
          : 'large',
        top: '24px',
      }}
    >
      <FlexGrid
        direction="column"
        wrap="nowrap"
        alignItems="center"
        margin="none"
        data-testid="info-headings"
      >
        <Box
          width="100%"
          maxWidth="998px"
          margin={{ horizontal: 'auto', bottom: 'none' }}
          alignContent="center"
          justify="center"
        >
          <SContainer
            data-testid={rest['data-testid']}
            style={rest.style}
            backgroundColor={rest.backgroundColor}
            textColor={rest.textColor}
            boderWidth={rest.boderWidth}
          >
            {icon ? (
              icon
            ) : (
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <mask
                  id="mask0_8323_47489"
                  style={{ maskType: 'alpha' }}
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="32"
                  height="32"
                >
                  <rect width="32" height="32" fill="#D9D9D9" />
                </mask>
                <g mask="url(#mask0_8323_47489)">
                  <path
                    d="M14.667 22.668H17.3337V14.668H14.667V22.668ZM16.0003 12.0013C16.3781 12.0013 16.6948 11.8735 16.9503 11.618C17.2059 11.3624 17.3337 11.0457 17.3337 10.668C17.3337 10.2902 17.2059 9.97352 16.9503 9.71797C16.6948 9.46241 16.3781 9.33464 16.0003 9.33464C15.6225 9.33464 15.3059 9.46241 15.0503 9.71797C14.7948 9.97352 14.667 10.2902 14.667 10.668C14.667 11.0457 14.7948 11.3624 15.0503 11.618C15.3059 11.8735 15.6225 12.0013 16.0003 12.0013ZM16.0003 29.3346C14.1559 29.3346 12.4225 28.9846 10.8003 28.2846C9.1781 27.5846 7.76699 26.6346 6.56699 25.4346C5.36699 24.2346 4.41699 22.8235 3.71699 21.2013C3.01699 19.5791 2.66699 17.8457 2.66699 16.0013C2.66699 14.1569 3.01699 12.4235 3.71699 10.8013C4.41699 9.17908 5.36699 7.76797 6.56699 6.56797C7.76699 5.36797 9.1781 4.41797 10.8003 3.71797C12.4225 3.01797 14.1559 2.66797 16.0003 2.66797C17.8448 2.66797 19.5781 3.01797 21.2003 3.71797C22.8225 4.41797 24.2337 5.36797 25.4337 6.56797C26.6337 7.76797 27.5837 9.17908 28.2837 10.8013C28.9837 12.4235 29.3337 14.1569 29.3337 16.0013C29.3337 17.8457 28.9837 19.5791 28.2837 21.2013C27.5837 22.8235 26.6337 24.2346 25.4337 25.4346C24.2337 26.6346 22.8225 27.5846 21.2003 28.2846C19.5781 28.9846 17.8448 29.3346 16.0003 29.3346ZM16.0003 26.668C18.9781 26.668 21.5003 25.6346 23.567 23.568C25.6337 21.5013 26.667 18.9791 26.667 16.0013C26.667 13.0235 25.6337 10.5013 23.567 8.43464C21.5003 6.36797 18.9781 5.33464 16.0003 5.33464C13.0225 5.33464 10.5003 6.36797 8.43366 8.43464C6.36699 10.5013 5.33366 13.0235 5.33366 16.0013C5.33366 18.9791 6.36699 21.5013 8.43366 23.568C10.5003 25.6346 13.0225 26.668 16.0003 26.668Z"
                    fill={rest.backgroundColor ? rest.textColor : `#196CFF`}
                  />
                </g>
              </svg>
            )}

            <div className="content">{children}</div>
          </SContainer>
        </Box>
      </FlexGrid>
    </Box>
  );
};
export default InfoBanner;
